<script>
import {ref} from "vue";
import IconCamera from "@js/components/Icons/IconCamera.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import IconClip from "@js/components/Icons/IconClip.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {useCallbackStore} from "@js/store/CallbackStore.js";

export default {
    name: "PhotoBanner",
    components: {
        OnlyMobile,
        IconClip,
        OnlyDesktop,
        IconCamera,
    },
    setup() {
        const callbackStore = useCallbackStore()
        const isDragOver = ref(false)

        const openCallbackWithFiles = (files) => {
            if (!files.length) return

            callbackStore.showBaseCallbackPopup({
                title: 'Оцените металлолом по фото',
                bitrix_title: 'Заявка на оценку металлолома по фото',
                comment: 'Клиент загрузил фото для предварительной оценки металлолома',
            })

            callbackStore.addBaseFormFiles(files)
        }

        const handleFiles = (fileList) => {
            const files = Array.from(fileList || []).filter(file => file?.type?.startsWith('image/'))
            if (!files.length) return

            openCallbackWithFiles(files)
        }

        const onFileChange = (e) => {
            const files = e.target.files
            if (files?.length) {
                handleFiles(files)
                e.target.value = null
            }
        }

        const onDragOver = (e) => {
            e.preventDefault()
            isDragOver.value = true
        }

        const onDragLeave = (e) => {
            e.preventDefault()
            isDragOver.value = false
        }

        const onDrop = (e) => {
            e.preventDefault()
            isDragOver.value = false

            const files = e.dataTransfer?.files
            if (files?.length) {
                handleFiles(files)
            }
        }

        return {
            isDragOver,
            onDragOver,
            onDragLeave,
            onDrop,
            onFileChange,
        }
    }
}
</script>

<template>
    <div class="p-5 md:p-10 w-full bg-grayBg rounded-3xl text-black">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-10 h-full md:min-h-[325px]">
            <div class="flex-1 flex flex-col max-md:hidden">
                <h2 class="mb-[30px] text-2xl font-bold">
                    <span class="underline-hover-effect underline-hover-pre-effect md:leading-10">
                        Узнайте стоимость металлолома<br/> по фото без звонка
                    </span>
                </h2>

                <ul class="list-disc list-outside space-y-3 ps-5 text-sm font-medium md:max-w-[80%] mt-auto">
                    <li>
                        Загрузите фото металлолома и получите предварительную оценку стоимости
                    </li>
                    <li>
                        Подходит для кабеля, цветного и черного металла, аккумуляторов, трансформаторов
                    </li>
                    <li>
                        Наши специалисты быстро определят тип металла и примерный вес
                    </li>
                    <li>
                        Вы узнаете ориентировочную цену и условия приёма
                    </li>
                    <li>
                        Можно отправить сразу несколько фото с разных ракурсов
                    </li>
                </ul>
            </div>

            <div>
                <div class="flex items-center justify-start w-full h-full">
                    <div class="flex items-center justify-center w-full h-full">
                        <OnlyDesktop
                            :maxSize="1024"
                            class="flex flex-col items-center justify-center w-full h-full dashed-bg rounded-3xl bg-transparent transition relative"
                            :class="{ 'drag-over': isDragOver }"
                            @dragover.prevent="onDragOver"
                            @dragenter.prevent="onDragOver"
                            @dragleave.prevent="onDragLeave"
                            @drop.prevent="onDrop"
                        >
                            <div class="flex flex-col items-center justify-center pt-5 pb-6 mb-5">
                                <IconCamera class="w-16 h-16 text-black"/>
                                <p class="mb-2 text-3xl font-semibold text-center">
                                    Оцените металлолом по фото
                                </p>
                                <p class="font-medium text-center">
                                    Загрузите одно или несколько фото
                                </p>
                            </div>

                            <label
                                class="inline-flex items-center justify-center gap-2 leading-tight px-8 py-4 bg-baseColor md:hover:bg-baseColor/90 rounded-lg cursor-pointer transition text-black font-medium mb-5">
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    class="hidden"
                                    autocomplete="off"
                                    @change="onFileChange"
                                />
                                <IconClip class="w-5 h-5 rotate-45"/>
                                Загрузить фото
                            </label>

                            <p class="font-medium text-sm text-center">
                                или перетащите несколько фото в пунктирное поле
                            </p>
                        </OnlyDesktop>

                        <OnlyMobile :maxSize="1024">
                            <div class="w-full flex flex-col items-center justify-between pt-5 pb-6 text-black">
                                <div class="flex flex-col items-center mb-10">
                                    <IconCamera class="w-16 h-16 text-gray-800"/>
                                    <p class="mb-2 text-2xl font-semibold text-center">
                                        Оцените металлолом по фото
                                    </p>
                                    <p class="font-semibold text-center">
                                        Загрузите одно или несколько фото
                                    </p>
                                </div>

                                <div class="flex flex-col items-center justify-center w-full space-y-4">
                                    <label
                                        class="w-full inline-flex items-center justify-center gap-2 leading-tight px-4 py-4 bg-baseColor rounded-lg cursor-pointer transition text-black font-medium">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            multiple
                                            class="hidden"
                                            @change="onFileChange"
                                        />
                                        <IconCamera class="w-5 h-5"/>
                                        Сфотографировать
                                    </label>

                                    <label
                                        class="w-full inline-flex items-center justify-center gap-2 leading-tight px-4 py-4 bg-white border border-gray-400 rounded-lg cursor-pointer md:hover:bg-opacity-30 transition text-black font-medium">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            class="hidden"
                                            @change="onFileChange"
                                        />
                                        <IconClip class="w-5 h-5 rotate-45"/>
                                        Загрузить фото
                                    </label>
                                </div>
                            </div>
                        </OnlyMobile>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.dashed-bg {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23000' stroke-dasharray='8, 10' stroke-dashoffset='7' stroke-linecap='square'/%3E%3C/svg%3E");
    transition: background-color 0.2s ease, transform 0.2s ease;
}

.dashed-bg.drag-over {
    background-color: rgba(0, 0, 0, 0.04);
    transform: scale(1.01);
}
</style>
